import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Link as LinkIcon, Image as ImageIcon,
  Minus, Eraser, ChevronDown, Type, Code2, X,
} from "lucide-react";

const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Satisfy&family=Caveat:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap";

const FONT_FAMILIES = [
  { label: "Default", value: "inherit" },
  { label: "Inter", value: "Inter, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', serif" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "─────────────", value: "", disabled: true },
  { label: "Dancing Script", value: "'Dancing Script', cursive" },
  { label: "Great Vibes", value: "'Great Vibes', cursive" },
  { label: "Pacifico", value: "'Pacifico', cursive" },
  { label: "Satisfy", value: "'Satisfy', cursive" },
  { label: "Caveat", value: "'Caveat', cursive" },
  { label: "Playfair Display", value: "'Playfair Display', serif" },
];

const FONT_SIZES = ["10","12","14","16","18","20","24","28","32","36","42","48","60","72"];

const HEADING_OPTIONS = [
  { label: "Normal text", value: "p", fs: 14 },
  { label: "Heading 1", value: "h1", fs: 28 },
  { label: "Heading 2", value: "h2", fs: 22 },
  { label: "Heading 3", value: "h3", fs: 18 },
  { label: "Heading 4", value: "h4", fs: 15 },
  { label: "Blockquote", value: "blockquote", fs: 14 },
  { label: "Monospace (Code)", value: "pre", fs: 12 },
];

const TEXT_COLORS = [
  "#000000","#1F2937","#374151","#6B7280","#9CA3AF",
  "#EF4444","#F97316","#F59E0B","#22C55E","#14B8A6",
  "#3B82F6","#8B5CF6","#EC4899","#FFFFFF","#FEF9C3",
];

const CODE_SNIPPETS = [
  { label: "Button", code: `<div style="text-align:center;margin:20px 0">\n  <a href="https://" style="background:#0044FF;color:#fff;padding:12px 28px;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block">Click Here</a>\n</div>` },
  { label: "Card", code: `<div style="background:#F8FAFC;border:1px solid #E2E8F0;border-radius:12px;padding:20px;margin:16px 0">\n  <h3 style="margin:0 0 8px;color:#0F172A">Card Title</h3>\n  <p style="margin:0;color:#475569">Card description text goes here.</p>\n</div>` },
  { label: "Divider", code: `<hr style="border:none;border-top:2px dashed #CBD5E1;margin:24px 0"/>` },
  { label: "2 Columns", code: `<table style="width:100%;border-collapse:collapse"><tr>\n  <td style="width:50%;padding:12px;vertical-align:top"><h4 style="margin:0 0 6px">Column 1</h4><p style="margin:0;color:#475569">Text here</p></td>\n  <td style="width:50%;padding:12px;vertical-align:top"><h4 style="margin:0 0 6px">Column 2</h4><p style="margin:0;color:#475569">Text here</p></td>\n</tr></table>` },
];

function Btn({ onClick, active, title, children, ...rest }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      {...rest}
      style={{
        display:"inline-flex",alignItems:"center",justifyContent:"center",
        width:28,height:28,borderRadius:6,border:"none",cursor:"pointer",
        background:active?"var(--brand)":"transparent",
        color:active?"#fff":"var(--text-soft)",
        transition:"all 0.13s",flexShrink:0,
      }}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <div style={{width:1,height:20,background:"var(--border)",margin:"0 4px",flexShrink:0,alignSelf:"center"}} />;
}

function ColorPicker({ colors, onSelect, onClose }) {
  return (
    <>
      <div style={{position:"fixed",inset:0,zIndex:9998}} onMouseDown={onClose} />
      <div style={{
        position:"absolute",top:"110%",left:0,zIndex:9999,
        background:"var(--surface)",border:"1px solid var(--border)",
        borderRadius:10,padding:10,boxShadow:"0 8px 28px rgba(0,0,0,0.4)",
        display:"grid",gridTemplateColumns:"repeat(5, 24px)",gap:4,marginTop:2,
      }}>
        {colors.map((c) => (
          <button key={c} type="button"
            onMouseDown={(e) => { e.preventDefault(); onSelect(c); onClose(); }}
            style={{width:24,height:24,borderRadius:4,background:c,border:"none",cursor:"pointer",
              outline:c==="#FFFFFF"?"1px solid #ccc":"none"}}
            title={c}
          />
        ))}
      </div>
    </>
  );
}

function RichTextEditor({ value, onChange, placeholder = "Start writing your email…", minHeight = 300 }) {
  const editorRef = useRef(null);
  const lastValueRef = useRef(value || "");
  const [showFont, setShowFont] = useState(false);
  const [showSize, setShowSize] = useState(false);
  const [showHeading, setShowHeading] = useState(false);
  const [showTextColor, setShowTextColor] = useState(false);
  const [showBgColor, setShowBgColor] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeInput, setCodeInput] = useState("");

  useEffect(() => {
    if (!document.getElementById("rte-gf")) {
      const l = document.createElement("link");
      l.id="rte-gf"; l.rel="stylesheet"; l.href=GOOGLE_FONTS_URL;
      document.head.appendChild(l);
    }
    if (!document.getElementById("rte-style")) {
      const s = document.createElement("style");
      s.id="rte-style";
      s.textContent=`
        [data-rte]:empty:before{content:attr(data-placeholder);color:#6B7280;font-style:italic;pointer-events:none;}
        [data-rte] h1{font-size:2em;font-weight:bold;margin:.4em 0}
        [data-rte] h2{font-size:1.5em;font-weight:bold;margin:.4em 0}
        [data-rte] h3{font-size:1.25em;font-weight:bold;margin:.4em 0}
        [data-rte] h4{font-size:1em;font-weight:bold;margin:.4em 0}
        [data-rte] blockquote{border-left:4px solid var(--brand);padding:8px 16px;margin:8px 0;opacity:.8}
        [data-rte] pre{background:rgba(0,0,0,.15);padding:12px;border-radius:8px;font-family:monospace;white-space:pre-wrap}
        [data-rte] a{color:var(--brand);text-decoration:underline}
        [data-rte] img{max-width:100%;border-radius:8px}
        [data-rte] ul{list-style:disc;padding-left:1.5em}
        [data-rte] ol{list-style:decimal;padding-left:1.5em}
      `;
      document.head.appendChild(s);
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && value !== lastValueRef.current) {
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || "";
        lastValueRef.current = value || "";
      }
    }
  }, [value]);

  const emit = () => {
    const html = editorRef.current?.innerHTML || "";
    lastValueRef.current = html;
    onChange(html);
  };

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    setTimeout(emit, 0);
    closeAll();
  };

  const q = (cmd) => { try { return document.queryCommandState(cmd); } catch { return false; } };
  const closeAll = () => { setShowFont(false); setShowSize(false); setShowHeading(false); setShowTextColor(false); setShowBgColor(false); };

  // Paste cleanup: strip Gmail/Word's inline styles/classes that cause overlapping,
  // mis-aligned content, keeping only structural/semantic HTML.
  const cleanPastedHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("style, script, meta, link, head, title, xml").forEach((el) => el.remove());
    doc.body.querySelectorAll("*").forEach((el) => {
      el.removeAttribute("style");
      el.removeAttribute("class");
      el.removeAttribute("id");
      el.removeAttribute("width");
      el.removeAttribute("height");
      el.removeAttribute("align");
      el.removeAttribute("valign");
      el.removeAttribute("lang");
      el.removeAttribute("dir");
    });
    return DOMPurify.sanitize(doc.body.innerHTML, {
      ALLOWED_TAGS: ["p","br","b","strong","i","em","u","strike","s","ul","ol","li","a","img","h1","h2","h3","h4","blockquote","pre","hr","span","div","table","thead","tbody","tr","td","th"],
      ALLOWED_ATTR: ["href","src","alt","target"],
    });
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    let clean;
    if (html && html.trim()) {
      clean = cleanPastedHtml(html);
    } else {
      clean = (text || "").split(/\r?\n/).map((l) => `<p>${l.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") || "<br>"}</p>`).join("");
    }
    document.execCommand("insertHTML", false, clean);
    setTimeout(emit, 0);
  };

  const insertCode = () => {
    if (!codeInput.trim()) { setShowCodeModal(false); return; }
    const clean = DOMPurify.sanitize(codeInput, { ADD_TAGS: ["style"], ADD_ATTR: ["style", "target"] });
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, clean);
    setTimeout(emit, 0);
    setShowCodeModal(false);
    setCodeInput("");
  };

  const applyFontFamily = (fontValue) => {
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      const range = sel.getRangeAt(0);
      const frag = range.extractContents();
      const span = document.createElement("span");
      span.style.fontFamily = fontValue;
      span.appendChild(frag);
      range.insertNode(span);
    } else {
      document.execCommand("fontName", false, fontValue);
    }
    setTimeout(emit, 0);
    setShowFont(false);
  };

  const applyFontSize = (size) => {
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      const range = sel.getRangeAt(0);
      const frag = range.extractContents();
      const span = document.createElement("span");
      span.style.fontSize = `${size}px`;
      span.appendChild(frag);
      range.insertNode(span);
    } else {
      document.execCommand("fontSize", false, "7");
      editorRef.current?.querySelectorAll("font[size='7']").forEach((el) => {
        el.removeAttribute("size"); el.style.fontSize = `${size}px`;
      });
    }
    setTimeout(emit, 0);
    setShowSize(false);
  };

  const pickerBtn = (label, isOpen, setOpen, icon) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); closeAll(); setOpen(!isOpen); }}
      style={{
        display:"flex",alignItems:"center",gap:4,padding:"0 8px",height:28,
        borderRadius:6,border:"1px solid var(--border)",background:"transparent",
        color:"var(--text-soft)",cursor:"pointer",fontSize:12,whiteSpace:"nowrap",
      }}
    >
      {icon}<span>{label}</span><ChevronDown size={11} />
    </button>
  );

  const dropBase = {
    position:"absolute",top:"110%",left:0,zIndex:9999,
    background:"var(--surface)",border:"1px solid var(--border)",
    borderRadius:10,padding:"6px 0",boxShadow:"0 8px 24px rgba(0,0,0,0.4)",
    marginTop:2,minWidth:160,
  };

  const dItem = {
    display:"block",width:"100%",textAlign:"left",padding:"5px 14px",
    border:"none",background:"transparent",color:"var(--text)",cursor:"pointer",
  };

  return (
    <div style={{border:"1px solid var(--border)",borderRadius:12,overflow:"visible",background:"var(--surface)"}}>
      <div
        style={{padding:"8px 12px",borderBottom:"1px solid var(--border)",background:"var(--bg)",
          display:"flex",flexWrap:"wrap",gap:3,alignItems:"center",borderRadius:"12px 12px 0 0"}}
        onMouseDown={(e) => e.preventDefault()}
      >
        {/* Heading/Style */}
        <div style={{position:"relative"}}>
          {pickerBtn("Style", showHeading, setShowHeading, <Type size={13}/>)}
          {showHeading && (
            <>
              <div style={{position:"fixed",inset:0,zIndex:9998}} onMouseDown={() => setShowHeading(false)} />
              <div style={{...dropBase,minWidth:190}}>
                {HEADING_OPTIONS.map((h) => (
                  <button key={h.value} type="button"
                    onMouseDown={(e) => { e.preventDefault(); exec("formatBlock",h.value); setShowHeading(false); }}
                    style={{...dItem,fontWeight:h.fs>=18?"bold":"normal",fontSize:Math.min(h.fs,22)}}>
                    {h.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <Sep />

        {/* Font */}
        <div style={{position:"relative"}}>
          {pickerBtn("Font", showFont, setShowFont, null)}
          {showFont && (
            <>
              <div style={{position:"fixed",inset:0,zIndex:9998}} onMouseDown={() => setShowFont(false)} />
              <div style={{...dropBase,maxHeight:280,overflowY:"auto",minWidth:210}}>
                {FONT_FAMILIES.map((f,i) => f.disabled
                  ? <div key={i} style={{padding:"3px 14px",color:"var(--text-xsoft)",fontSize:11}}>{f.label}</div>
                  : <button key={f.value} type="button"
                      onMouseDown={(e) => { e.preventDefault(); applyFontFamily(f.value); }}
                      style={{...dItem,fontFamily:f.value!=="inherit"?f.value:undefined,fontSize:13}}>
                      {f.label}
                    </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Size */}
        <div style={{position:"relative"}}>
          {pickerBtn("Size", showSize, setShowSize, null)}
          {showSize && (
            <>
              <div style={{position:"fixed",inset:0,zIndex:9998}} onMouseDown={() => setShowSize(false)} />
              <div style={{...dropBase,maxHeight:250,overflowY:"auto",minWidth:90}}>
                {FONT_SIZES.map((s) => (
                  <button key={s} type="button"
                    onMouseDown={(e) => { e.preventDefault(); applyFontSize(s); }}
                    style={{...dItem,fontSize:Math.min(parseInt(s),20)}}>
                    {s}px
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <Sep />

        {/* Code — paste raw HTML/CSS with a live preview */}
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); closeAll(); setShowCodeModal(true); }}
          data-testid="rte-code-btn"
          title="Paste custom HTML/CSS code and preview it live"
          style={{
            display:"flex",alignItems:"center",gap:5,padding:"0 10px",height:28,
            borderRadius:6,border:"1px solid var(--brand)",
            background:"color-mix(in srgb, var(--brand) 12%, transparent)",
            color:"var(--brand)",cursor:"pointer",fontSize:12,fontWeight:600,whiteSpace:"nowrap",
          }}
        >
          <Code2 size={13}/><span>Code</span>
        </button>

        <Sep />

        <Btn onClick={() => exec("bold")} active={q("bold")} title="Bold (Ctrl+B)"><Bold size={13}/></Btn>
        <Btn onClick={() => exec("italic")} active={q("italic")} title="Italic (Ctrl+I)"><Italic size={13}/></Btn>
        <Btn onClick={() => exec("underline")} active={q("underline")} title="Underline (Ctrl+U)"><Underline size={13}/></Btn>
        <Btn onClick={() => exec("strikeThrough")} active={q("strikeThrough")} title="Strikethrough"><Strikethrough size={13}/></Btn>

        <Sep />

        <Btn onClick={() => exec("justifyLeft")} active={q("justifyLeft")} title="Align left"><AlignLeft size={13}/></Btn>
        <Btn onClick={() => exec("justifyCenter")} active={q("justifyCenter")} title="Center"><AlignCenter size={13}/></Btn>
        <Btn onClick={() => exec("justifyRight")} active={q("justifyRight")} title="Align right"><AlignRight size={13}/></Btn>
        <Btn onClick={() => exec("justifyFull")} active={q("justifyFull")} title="Justify"><AlignJustify size={13}/></Btn>

        <Sep />

        <Btn onClick={() => exec("insertUnorderedList")} active={q("insertUnorderedList")} title="Bullet list"><List size={13}/></Btn>
        <Btn onClick={() => exec("insertOrderedList")} active={q("insertOrderedList")} title="Numbered list"><ListOrdered size={13}/></Btn>

        <Sep />

        <div style={{position:"relative"}}>
          <Btn onClick={() => { closeAll(); setShowTextColor(!showTextColor); }} title="Text color">
            <span style={{fontWeight:"bold",fontSize:12,fontFamily:"serif"}}>A</span>
          </Btn>
          {showTextColor && <ColorPicker colors={TEXT_COLORS} onSelect={(c) => exec("foreColor",c)} onClose={() => setShowTextColor(false)} />}
        </div>
        <div style={{position:"relative"}}>
          <Btn onClick={() => { closeAll(); setShowBgColor(!showBgColor); }} title="Highlight">
            <span style={{fontSize:10,fontWeight:"bold"}}>BG</span>
          </Btn>
          {showBgColor && <ColorPicker colors={TEXT_COLORS} onSelect={(c) => exec("hiliteColor",c)} onClose={() => setShowBgColor(false)} />}
        </div>

        <Sep />

        <Btn onClick={() => { const url=window.prompt("URL:","https://"); if(url) exec("createLink",url); }} title="Insert link"><LinkIcon size={13}/></Btn>
        <Btn onClick={() => { const url=window.prompt("Image URL:","https://"); if(url) exec("insertImage",url); }} title="Insert image"><ImageIcon size={13}/></Btn>
        <Btn onClick={() => exec("insertHTML","<hr style='border:none;border-top:2px solid #e5e7eb;margin:20px 0;'/>")} title="Divider"><Minus size={13}/></Btn>

        <Sep />

        <Btn onClick={() => exec("removeFormat")} title="Clear formatting"><Eraser size={13}/></Btn>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        data-rte
        data-testid="rte-editor"
        data-placeholder={placeholder}
        onInput={emit}
        onPaste={handlePaste}
        style={{
          minHeight, padding:"16px 20px", outline:"none",
          color:"var(--text)", lineHeight:1.75, fontSize:15,
          background:"transparent", borderRadius:"0 0 12px 12px",
        }}
      />

      {showCodeModal && (
        <div
          style={{ position:"fixed", inset:0, zIndex:10000, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onMouseDown={() => setShowCodeModal(false)}
          data-testid="rte-code-modal"
        >
          <div onMouseDown={(e) => e.stopPropagation()} style={{ background:"var(--surface)", borderRadius:14, padding:20, width:"min(920px, 100%)", maxHeight:"85vh", overflow:"auto", border:"1px solid var(--border)" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <div style={{ fontWeight:700, fontSize:15, color:"var(--text)" }}>Paste custom HTML / CSS code</div>
              <button type="button" onClick={() => setShowCodeModal(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-soft)" }} data-testid="rte-code-close-btn"><X size={16}/></button>
            </div>
            <p style={{ fontSize:12, color:"var(--text-soft)", marginBottom:10 }}>
              Paste raw HTML/CSS below — it renders live on the right, section by section. Note: <code>&lt;script&gt;</code> tags won&apos;t run in email clients, so keep interactive behavior to plain HTML/CSS.
            </p>
            <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
              <span style={{ fontSize:11, color:"var(--text-soft)", alignSelf:"center", marginRight:2 }}>Quick insert:</span>
              {CODE_SNIPPETS.map((s) => (
                <button
                  key={s.label} type="button"
                  data-testid={`rte-code-snippet-${s.label.toLowerCase().replace(/\s+/g,"-")}`}
                  onClick={() => setCodeInput(s.code)}
                  style={{ fontSize:11, fontWeight:600, padding:"4px 10px", borderRadius:20, border:"1px solid var(--border)", background:"var(--bg)", color:"var(--text)", cursor:"pointer" }}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
              <div>
                <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:"var(--text-soft)" }}>CODE</div>
                <textarea
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  rows={16}
                  data-testid="rte-code-textarea"
                  placeholder={'<div style="padding:16px;background:#f3f4f6;border-radius:8px">\n  <h2>Custom block</h2>\n</div>'}
                  style={{ width:"100%", fontFamily:"monospace", fontSize:12, padding:10, borderRadius:8, border:"1px solid var(--border)", background:"var(--bg)", color:"var(--text)", resize:"vertical" }}
                />
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:600, marginBottom:6, color:"var(--text-soft)" }}>LIVE PREVIEW</div>
                <div
                  data-testid="rte-code-preview"
                  style={{ minHeight:340, maxHeight:400, overflow:"auto", padding:12, borderRadius:8, border:"1px solid var(--border)", background:"#fff", color:"#0F172A" }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(codeInput, { ADD_TAGS:["style"], ADD_ATTR:["style"] }) }}
                />
              </div>
            </div>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:16 }}>
              <button type="button" onClick={() => setShowCodeModal(false)} data-testid="rte-code-cancel-btn" style={{ padding:"8px 16px", borderRadius:8, border:"1px solid var(--border)", background:"transparent", color:"var(--text)", cursor:"pointer" }}>Cancel</button>
              <button type="button" onClick={insertCode} data-testid="rte-code-insert-btn" style={{ padding:"8px 16px", borderRadius:8, border:"none", background:"var(--brand)", color:"#fff", cursor:"pointer", fontWeight:600 }}>Insert into email</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RichTextEditor;
export { RichTextEditor };
